"use client";

import { Alert, Input, Label, Separator, TextField } from "@heroui/react";
import NextLink from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { GoogleButton } from "@/components/auth/google-button";
import { SubmitButton } from "@/components/auth/submit-button";
import { signIn } from "@/lib/auth-client";

export function LoginForm({ next }: { next?: string }) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isPending, setIsPending] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setIsPending(true);

    const form = new FormData(event.currentTarget);

    const { data, error: signInError } = await signIn.email({
      email: String(form.get("email") ?? ""),
      password: String(form.get("password") ?? ""),
      callbackURL: next || "/dashboard",
    });

    if (signInError) {
      // 403 is Better Auth's specific "email not verified" response; every
      // other failure stays deliberately vague, so this form can't be used to
      // discover which addresses have accounts.
      if (signInError.status === 403) {
        setNeedsVerification(true);
      } else {
        setError("That email and password combination didn't work.");
      }
      setIsPending(false);
      return;
    }

    // The password was right but the account has two-factor on: there is no
    // session yet, only a pending challenge. Sending them to /dashboard here
    // would bounce straight back to /login with no explanation.
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      router.push(next ? `/two-factor?next=${encodeURIComponent(next)}` : "/two-factor");
      return;
    }

    router.push(next || "/dashboard");
    router.refresh();
  }

  return (
    <div className="flex flex-col gap-5">
      <GoogleButton next={next} />

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted text-xs">or</span>
        <Separator className="flex-1" />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error ? (
          <Alert status="danger">
            <Alert.Content>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Content>
          </Alert>
        ) : null}

        {needsVerification ? (
          <Alert status="warning">
            <Alert.Content>
              <Alert.Title>Verify your email first</Alert.Title>
              <Alert.Description>
                We sent a link when you signed up. Check your inbox, then sign in
                again.
              </Alert.Description>
            </Alert.Content>
          </Alert>
        ) : null}

        <TextField name="email" type="email" isRequired autoComplete="email">
          <Label>Email</Label>
          <Input placeholder="you@company.com" />
        </TextField>

        <TextField
          name="password"
          type="password"
          isRequired
          autoComplete="current-password"
        >
          <div className="flex items-center justify-between">
            <Label>Password</Label>
            <NextLink
              href="/forgot-password"
              className="text-muted hover:text-foreground text-xs"
            >
              Forgot password?
            </NextLink>
          </div>
          <Input placeholder="••••••••••" />
        </TextField>

        <SubmitButton isPending={isPending}>Sign in</SubmitButton>
      </form>
    </div>
  );
}
